import Link from 'next/link'
import { useRouter } from 'next/router'
import React from 'react'
import styled from 'styled-components'

import { NEWSLIST_NEWS_LIMIT } from '../../../config/settings'
import { Maybe, NewsCollectionFieldsFragment } from '../../../generated/graphql'
import theme from '../../../theme'
import toDisplayDateText from '../../../utils/DateFormat'
import Media from '../../../utils/Media'
import { Container } from '../../shared/Container'
import Layout from '../../shared/Layout'
import PageHeader from '../../shared/PageHeader'
import Pagination from '../../shared/Pagination'
import Section from '../../shared/Section'

const getYearText = (dateString: String) => dateString.split('-')[0]

interface StaticNewIndexProps {
  newsItems: Maybe<NewsCollectionFieldsFragment>[]
}

interface StringKeyObject {
  [key: string]: any
}

const NewsArchiveTemplate: React.FC<StaticNewIndexProps> = ({ newsItems }) => {
  const router = useRouter()

  let addParam = ''
  let currentPage = 1
  const showRange = NEWSLIST_NEWS_LIMIT
  let filteredItemList = []

  let defaultSelectorValue: string = 'all'
  let defaultSelectorCategoryValue: string = 'all'

  if (router.query.page !== undefined) {
    currentPage = parseInt(router.query.page.toString(), 10)
  }
  currentPage -= 1

  // カテゴリーの絞り込み
  if (router.query.category !== undefined) {
    const currentCategory = router.query.category
    addParam += `&category=${currentCategory}`
    defaultSelectorCategoryValue = String(currentCategory)
    filteredItemList = newsItems.filter((row) => {
      const cat = (row as any)?.category
      const slug = (cat as any)?.slug
      return slug === currentCategory
    })
  } else {
    filteredItemList = newsItems
  }

  // 年の絞り込み
  if (router.query.year !== undefined) {
    const currentYear = router.query.year
    addParam += `&year=${currentYear}`
    defaultSelectorValue = String(currentYear)
    filteredItemList = filteredItemList.filter((row) => {
      const date = (row as any)?.date
      return getYearText(date) === currentYear
    })
  } else if (filteredItemList.length === 0) filteredItemList = newsItems

  // ページの絞り込み
  const total = filteredItemList.length
  filteredItemList = filteredItemList.filter((item, i) => {
    if (
      currentPage * showRange <= i &&
      i < currentPage * showRange + showRange
    ) {
      return item
    }
    return false
  })

  const onChangeSelectYear = (value: string) => {
    if (value === 'all') {
      router.push('/news?page=1', undefined, { scroll: false })
    } else {
      router.push(`/news?year=${value}`, undefined, { scroll: false })
    }
  }

  const onChangeSelectCategory = (value: string) => {
    if (value === 'all') {
      router.push('/news?page=1')
    } else {
      router.push(`/news?page=1&category=${value}`)
    }
  }

  const getArrayDeleteDouble1 = (ary: StringKeyObject[]) => {
    const key: string = 'id'
    const validate: any = []
    const returnValues: any = []
    ary.forEach((e) => {
      if (validate.indexOf(e[key]) === -1) {
        validate.push(e[key])
        returnValues.push(e)
      }
    })
    return returnValues
  }

  const arrayMoveAt = (array: any[], index: number, at: number) => {
    if (index === at || index > array.length - 1 || at > array.length - 1) {
      return array
    }
    const value = array[index]
    const tail = array.slice(index + 1)
    array.splice(index)
    Array.prototype.push.apply(array, tail)
    array.splice(at, 0, value)
    return array
  }

  // 年の重複無し配列を作る
  let yearStringSet: StringKeyObject[] = []
  let categoryStringSet: StringKeyObject[] = []
  newsItems.forEach((d: any) => {
    if (d.category !== null) {
      categoryStringSet.push({
        id: getYearText(d.category.slug),
        title: getYearText(d.category.name),
      })
    }

    yearStringSet.push({
      id: getYearText(d.date),
      title: getYearText(d.date),
    })
  })
  categoryStringSet = getArrayDeleteDouble1(categoryStringSet)
  yearStringSet = getArrayDeleteDouble1(yearStringSet)
  yearStringSet.sort()

  let otherIndex = 0
  for (let i = 0; i < categoryStringSet.length; i += 1) {
    if (categoryStringSet[i].title === 'その他') {
      otherIndex = i
      break
    }
  }
  categoryStringSet = arrayMoveAt(
    categoryStringSet,
    otherIndex,
    categoryStringSet.length - 1
  )

  let addParamString: string = ''
  if (defaultSelectorCategoryValue !== 'all') {
    addParamString = `&category=${defaultSelectorCategoryValue}`
  }

  return (
    <Layout>
      <PageHeader
        label="お知らせ"
        backgroundImage="/images/picture/news_hero.jpg"
      />
      <StyledSection>
        <Container size="large">
          <FilterUI>
            <div>
              <SortTitle>カテゴリー別</SortTitle>
              <TagList>
                <Link href="/news?page=1">
                  {router.query.category === undefined ? (
                    <CurrentTag>全カテゴリー</CurrentTag>
                  ) : (
                    <Tag>全カテゴリー</Tag>
                  )}
                </Link>
                {categoryStringSet.map((obj) => (
                  <Link
                    href={`/news?page=1&category=${obj.id}`}
                    key={`category-select-${obj.id}`}
                  >
                    {router.query.category !== undefined &&
                    router.query.category === obj.id ? (
                      <CurrentTag>{obj.title}</CurrentTag>
                    ) : (
                      <Tag>{obj.title}</Tag>
                    )}
                  </Link>
                ))}
              </TagList>
              <TagSelect>
                <Selector
                  value={defaultSelectorCategoryValue}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    onChangeSelectCategory(event.target.value)
                  }}
                >
                  <option value="all">全カテゴリー</option>
                  {categoryStringSet.map((obj) => (
                    <option value={obj.id} key={`select-category-${obj.id}`}>
                      {obj.title}
                    </option>
                  ))}
                </Selector>
              </TagSelect>
            </div>

            <div>
              <SortTitle>年別</SortTitle>
              <TagList>
                <Link href={`/news?page=1${addParamString}`} scroll={false}>
                  {router.query.year === undefined ? (
                    <CurrentTag>全て</CurrentTag>
                  ) : (
                    <Tag>全て</Tag>
                  )}
                </Link>
                {yearStringSet.map((obj) => (
                  <Link
                    href={`/news?year=${obj.id}${addParamString}`}
                    key={`year-select-${obj.id}`}
                    scroll={false}
                  >
                    {router.query.year !== undefined &&
                    router.query.year === obj.id ? (
                      <CurrentTag>{obj.title}年</CurrentTag>
                    ) : (
                      <Tag>{obj.title}年</Tag>
                    )}
                  </Link>
                ))}
              </TagList>
              <TagSelect>
                <Selector
                  value={defaultSelectorValue}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    onChangeSelectYear(event.target.value)
                  }}
                >
                  <option value="all">全年代</option>
                  {yearStringSet.map((obj) => (
                    <option value={obj.title} key={`select-year-${obj.title}`}>
                      {obj.title}年
                    </option>
                  ))}
                </Selector>
              </TagSelect>
            </div>
          </FilterUI>

          <List>
            {filteredItemList.map((item) => {
              if (item !== null) {
                const { sys } = item
                const id = (sys as any)?.id
                const date = (item as any)?.date
                const title = (item as any)?.title
                const url = `/news/${id}`
                const categoryName = item?.category?.name || 'その他'
                return (
                  <li key={id}>
                    <NewsListItem href={url} passHref>
                      <NewsListItemTag>
                        <TagBase>{categoryName}</TagBase>
                      </NewsListItemTag>
                      <NewsListItemDate>
                        {toDisplayDateText(date)}
                      </NewsListItemDate>
                      <NewsListItemTitle>
                        <p>{title}</p>
                      </NewsListItemTitle>
                    </NewsListItem>
                  </li>
                )
              }
              return <></>
            })}
          </List>
          <Pagination
            sum={total}
            per={NEWSLIST_NEWS_LIMIT}
            baseUrl="/news?page="
            currentPage={currentPage + 1}
            addParam={addParam}
          />
        </Container>
      </StyledSection>
    </Layout>
  )
}

export default NewsArchiveTemplate

const FilterUI = styled.div`
  & > div + div {
    margin-top: 30px;
  }

  ${Media.mobileMiddle} {
    display: flex;

    & > div {
      position: relative;
      width: 100%;

      &::after {
        content: '';
        display: block;
        position: absolute;
        right: 16px;
        bottom: 14px;
        width: 8px;
        height: 8px;
        transform: rotate(45deg);
        border-right: 1px solid #fff;
        border-bottom: 1px solid #fff;
      }
    }
    & > div + div {
      margin-top: 0;
      margin-left: 10px;
    }
  }
`

const Selector = styled.select`
  display: block;
  appearance: none;
  width: 100%;
  height: 32px;
  border-radius: 9999px 9999px;
  background-color: #000;
  color: #fff;
  font-family: 'var(--font-noto-sans-jp)', sans-serif;
  font-size: 16px;
  text-align: -webkit-center;

  ${Media.mobileMiddle} {
    padding: 0 12px;
    font-size: 14px;
    font-weight: 700;
    text-align: center;
  }
`

const TagSelect = styled.div`
  display: none;
  justify-content: center;

  ${Media.mobileMiddle} {
    display: flex;
  }
`

const StyledSection = styled(Section)`
  padding: 0 0 150px;

  ${Media.mobileMiddle} {
    padding: 0 0 100px;
  }
`

const SortTitle = styled.div`
  ${theme.typography.DefNotoSansBodyBold}
  margin-bottom: 12px;
  text-align: center;
`

const TagList = styled.div`
  display: flex;
  justify-content: center;
  margin-right: -18px;
  margin-left: -18px;

  & > * {
    margin-right: 18px;
    margin-left: 18px;
    text-decoration: none;
  }
  a {
    color: #000;
    text-decoration: none;
  }
  .CurrentTag {
    color: #fff;
  }

  ${Media.mobileMiddle} {
    display: none;
  }
`

const List = styled.ul`
  margin-top: 73px;
  margin-bottom: 56px;
  & > li {
    border-bottom: 1px solid #000;
  }

  ${Media.mobileMiddle} {
    margin-top: 30px;
  }
`

const TagBase = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 144px;
  height: 30px;
  padding: 0 10px;
  border: 1px solid #000;
  border-radius: 15px 15px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.05em;
`

const NewsListItemTag = styled.div`
  display: flex;
  align-items: center;
  width: 176px;
`
const Tag = styled(TagBase)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 144px;
  height: 30px;
  padding: 0 10px;
  border: 1px solid #000;
  border-radius: 15px 15px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.05em;

  &:hover {
    background-color: #000;
    color: #fff;
  }
`
const CurrentTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 144px;
  height: 30px;
  padding: 0 10px;
  border: 1px solid #000;
  border-radius: 15px 15px;
  background-color: #000;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.05em;
`

const NewsListItemDate = styled.div`
  ${theme.typography.DefNotoSansBody}
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 176px;
`
const NewsListItemTitle = styled.div`
  ${theme.typography.DefNotoSansBody}
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: calc(100% - 352px);

  ${Media.mobileMiddle} {
    width: 100%;
    margin-top: 19px;
  }
`

const NewsListItem = styled(Link)`
  display: flex;
  align-items: center;
  min-height: 80px;
  padding: 6px 0;
  color: #000;
  text-decoration: none;

  &:hover {
    color: #1e9af3;

    ${TagBase} {
      background-color: #000;
      color: #fff;
    }
  }

  ${Media.mobileMiddle} {
    flex-wrap: wrap;
    padding-top: 29px;
  }
`
